'use client';

import * as React from 'react';

/**
 * Merges refs into a single memoized callback ref or `null`.
 *
 * ```tsx
 * const rootRef = React.useRef<Instance>(null);
 * const refFork = useForkRef(rootRef, props.ref);
 *
 * return (
 *   <Root {...props} ref={refFork} />
 * );
 * ```
 *
 * @param {Array<React.Ref<Instance> | undefined>} refs The ref array.
 * @returns {React.RefCallback<Instance> | null} The new ref callback.
 */
export default function useForkRef() {
  for (var _len = arguments.length, refs = new Array(_len), _key = 0; _key < _len; _key++) {
    refs[_key] = arguments[_key];
  }
  var cleanupRef = React.useRef(undefined);
  var refEffect = React.useCallback(function (instance) {
    var cleanups = refs.map(function (ref) {
      if (ref == null) {
        return null;
      }
      if (typeof ref === 'function') {
        var refCallback = ref;
        var refCleanup = refCallback(instance);
        return typeof refCleanup === 'function' ? refCleanup : function () {
          refCallback(null);
        };
      }
      ref.current = instance;
      return function () {
        ref.current = null;
      };
    });
    return function () {
      cleanups.forEach(function (refCleanup) {
        return refCleanup === null || refCleanup === void 0 ? void 0 : refCleanup();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refs);
  return React.useMemo(function () {
    if (refs.every(function (ref) {
      return ref == null;
    })) {
      return null;
    }
    return function (value) {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
      if (value != null) {
        cleanupRef.current = refEffect(value);
      }
    };
    // TODO: uncomment once we enable eslint-plugin-react-compiler // eslint-disable-next-line react-compiler/react-compiler -- intentionally ignoring that the dependency array must be an array literal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, refs);
}